// client/src/pages/admin/ManageGiftEmailTemplates.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';
import Modal from '../../components/ui/Modal.js';

export default function ManageGiftEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    name: '',
    htmlcontent: '',
    isdefault: false,
    isactive: true,
  });

  const load = async () => {
    try {
      const data = await api.get('/gifts/admin/email-templates');
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Load gift email templates error:', err);
    }
  };

  useEffect(() => { load(); }, []);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', htmlcontent: '', isdefault: false, isactive: true });
    setShowForm(true);
  };

  const startEdit = (t) => {
    setEditing(t.templateid);
    setForm({
      name: t.name,
      htmlcontent: t.htmlcontent || '',
      isdefault: t.isdefault || false,
      isactive: t.isactive !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/gifts/admin/email-templates/${editing}`, form);
        showMessage('Template updated');
      } else {
        await api.post('/gifts/admin/email-templates', form);
        showMessage('Template created');
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      showMessage(err.data?.error || 'Failed to save template', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gift email template?')) return;
    try {
      await api.delete(`/gifts/admin/email-templates/${id}`);
      showMessage('Template deleted');
      load();
    } catch (err) {
      showMessage(err.data?.error || 'Failed to delete', 'error');
    }
  };

  // Build a preview with sample data
  const buildSamplePreview = (html) => {
    if (!html) return '<p style="padding:20px;color:#888;">No HTML content</p>';
    let preview = html;
    preview = preview.replace(/\{\{site_name\}\}/g, 'The Cherry Fairy Shop');
    preview = preview.replace(/\{\{recipient_name\}\}/g, 'Sarah');
    preview = preview.replace(/\{\{sender_name\}\}/g, 'Harold Stong');
    preview = preview.replace(/\{\{salutation\}\}/g, 'Dear Sarah,');
    preview = preview.replace(/\{\{closing\}\}/g, 'With love, Harold Stong');
    preview = preview.replace(/\{\{personal_message\}\}/g, 'I hope you enjoy sending beautiful eCards to all your friends and family!');
    preview = preview.replace(/\{\{redeem_url\}\}/g, '#');
    preview = preview.replace(/\{\{gift_code\}\}/g, 'gift-85c327d8012de92c');
    preview = preview.replace(/\{\{plan_name\}\}/g, 'Fairydust');
    preview = preview.replace(/\{\{duration_label\}\}/g, '1 year');
    preview = preview.replace(/\{\{code_expires_date\}\}/g, 'March 13, 2027');
    return preview;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-gold">Gift Email Templates</h1>
          <p className="text-royal-200 text-sm mt-1">Manage the email templates sent when someone gifts a subscription</p>
        </div>
        <Button onClick={openNew}>Add Template</Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          message.type === 'error' ? 'bg-red-900/30 border border-red-700 text-red-400' : 'bg-green-900/30 border border-green-700 text-green-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* Templates list */}
      <div className="space-y-3">
        {templates.map((t) => (
          <div key={t.templateid} className={`bg-royal-800/40 border rounded-lg p-4 flex justify-between items-center ${
            t.isdefault ? 'border-gold/50' : 'border-royal-600'
          }`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white font-semibold">{t.name}</p>
                {t.isdefault && (
                  <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full">Default</span>
                )}
                {!t.isactive && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Inactive</span>
                )}
              </div>
              <p className="text-royal-200 text-sm mt-0.5">
                Updated {new Date(t.updatedat || t.createdat).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button
                className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                onClick={() => setShowPreview(t)}
              >
                Preview
              </button>
              <button className="text-gold text-sm hover:text-gold-light transition-colors" onClick={() => startEdit(t)}>Edit</button>
              <button className="text-red-400 text-sm hover:text-red-300 transition-colors" onClick={() => handleDelete(t.templateid)}>Delete</button>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-royal-200 text-base mb-3">No gift email templates yet.</p>
            <p className="text-royal-200 text-sm mb-4">Create one and mark it as default to use for all gift emails.</p>
            <Button onClick={openNew}>Create First Template</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Gift Email Template' : 'New Gift Email Template'} maxWidth="max-w-4xl">
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto pr-2 space-y-4">
          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Template Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="e.g. Default Gift Email"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">HTML Content</label>
            <textarea
              value={form.htmlcontent}
              onChange={(e) => setForm({ ...form, htmlcontent: e.target.value })}
              required
              rows={16}
              placeholder="Paste your complete HTML email template here..."
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors font-mono resize-y"
            />
          </div>

          {/* Placeholder reference */}
          <div className="p-3 bg-royal-900/50 rounded-lg">
            <p className="text-royal-200 text-xs font-semibold mb-2">Available Placeholders:</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gold font-mono">{'{{site_name}}'}</span><span className="text-royal-200">App name</span>
              <span className="text-gold font-mono">{'{{recipient_name}}'}</span><span className="text-royal-200">Recipient name</span>
              <span className="text-gold font-mono">{'{{sender_name}}'}</span><span className="text-royal-200">Sender name</span>
              <span className="text-gold font-mono">{'{{salutation}}'}</span><span className="text-royal-200">e.g. "Dear Sarah,"</span>
              <span className="text-gold font-mono">{'{{closing}}'}</span><span className="text-royal-200">e.g. "With love, Harold"</span>
              <span className="text-gold font-mono">{'{{personal_message}}'}</span><span className="text-royal-200">Buyer's message</span>
              <span className="text-gold font-mono">{'{{redeem_url}}'}</span><span className="text-royal-200">Link to claim gift</span>
              <span className="text-gold font-mono">{'{{gift_code}}'}</span><span className="text-royal-200">Gift code string</span>
              <span className="text-gold font-mono">{'{{plan_name}}'}</span><span className="text-royal-200">e.g. "Fairydust"</span>
              <span className="text-gold font-mono">{'{{duration_label}}'}</span><span className="text-royal-200">e.g. "1 year"</span>
              <span className="text-gold font-mono">{'{{code_expires_date}}'}</span><span className="text-royal-200">Expiry date</span>
            </div>
          </div>

          {/* Live preview */}
          {form.htmlcontent && (
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Preview (with sample data)</label>
              <div className="rounded-lg border border-royal-600 overflow-hidden">
                <iframe
                  srcDoc={buildSamplePreview(form.htmlcontent)}
                  title="Gift Email Preview"
                  className="w-full border-0 bg-white"
                  style={{ minHeight: '400px', display: 'block' }}
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-gold text-sm">
              <input
                type="checkbox"
                checked={form.isdefault}
                onChange={(e) => setForm({ ...form, isdefault: e.target.checked })}
              />
              Default (used for all gift emails)
            </label>
            <label className="flex items-center gap-2 text-royal-200 text-sm">
              <input
                type="checkbox"
                checked={form.isactive}
                onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
              />
              Active
            </label>
          </div>

          {form.isdefault && (
            <p className="text-gold/80 text-xs italic">
              Setting this as default will remove default status from any other template.
            </p>
          )}

          <Button type="submit">{editing ? 'Update Template' : 'Create Template'}</Button>
        </form>
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={!!showPreview} onClose={() => setShowPreview(null)} title={`Preview: ${showPreview?.name || ''}`} maxWidth="max-w-3xl">
        {showPreview && (
          <div>
            <p className="text-royal-200 text-xs mb-3 italic">Showing with sample data. Actual emails will use real recipient and gift information.</p>
            <div className="rounded-lg border border-royal-600 overflow-hidden">
              <iframe
                srcDoc={buildSamplePreview(showPreview.htmlcontent)}
                title="Gift Email Preview"
                className="w-full border-0 bg-white"
                style={{ minHeight: '500px', display: 'block' }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}